
export class AudioManager {
  private static instance: AudioManager | null = null;
  private audio: HTMLAudioElement | null = null;
  private audioUrl: string | null = null;
  private activeButtonId: string | null = null;
  private stateChangeCallbacks: Map<string, () => void> = new Map();
  private retryAttempts: Map<string, number> = new Map();
  private maxRetries = 3;

  private constructor() {}

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  registerStateChangeCallback(buttonId: string, callback: () => void) {
    this.stateChangeCallbacks.set(buttonId, callback);
  }

  unregisterStateChangeCallback(buttonId: string) {
    this.stateChangeCallbacks.delete(buttonId);
    this.retryAttempts.delete(buttonId);
  }

  private notifyStateChange() {
    this.stateChangeCallbacks.forEach((callback) => callback());
  }

  cleanup() {
    if (this.audio) {
      this.audio.onended = null;
      this.audio.onpause = null;
      this.audio.onplay = null;
      this.audio.onerror = null;
      
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio.src = '';
      this.audio = null;
    }
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
      this.audioUrl = null;
    }
    const previousButtonId = this.activeButtonId;
    this.activeButtonId = null;
    this.retryAttempts.clear();
    if (previousButtonId) {
      this.notifyStateChange();
    }
  }

  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.cleanup();
  }

  setActiveButton(buttonId: string | null) {
    if (this.activeButtonId !== buttonId) {
      this.stop();
    }
    this.activeButtonId = buttonId;
    this.notifyStateChange();
  }

  getActiveButton(): string | null {
    return this.activeButtonId;
  }

  private shouldRetry(buttonId: string): boolean {
    const attempts = this.retryAttempts.get(buttonId) || 0;
    if (attempts < this.maxRetries) {
      this.retryAttempts.set(buttonId, attempts + 1);
      return true;
    }
    return false;
  }

  async play(base64Audio: string, buttonId: string): Promise<HTMLAudioElement | null> {
    try {
      this.stop();
      this.setActiveButton(buttonId);

      const audioBlob = new Blob(
        [Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      this.audioUrl = URL.createObjectURL(audioBlob);
      this.audio = new Audio(this.audioUrl);
      this.audio.preload = 'auto';

      this.audio.onended = () => {
        console.log('Audio playback ended');
        this.cleanup();
      };

      this.audio.onpause = () => {
        console.log('Audio playback paused');
        this.cleanup();
      };

      this.audio.onerror = (e) => {
        const target = e instanceof Event ? e.target as HTMLAudioElement : null;
        
        if (target?.error?.code === 3 && this.shouldRetry(buttonId)) { // MEDIA_ERR_DECODE
          console.log('Retrying audio playback...');
          setTimeout(() => this.play(base64Audio, buttonId), 1000);
          return;
        }
        
        console.error('Audio playback error:', e);
        this.cleanup();
      };

      return this.audio;
    } catch (error) {
      console.error('Error creating audio:', error);
      this.cleanup();
      return null;
    }
  }
}
